import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, AutocompletePropsSizeOverrides, TextField } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import debounce from 'lodash.debounce';
import { INPUT_STYLE } from '../../../utils/styleHelpers';
import { getConfiguration } from '../../../store/Configuration';
import { withGoogleMapsApiKey } from '../../../utils/withGoogleMapsApiKey';
import { OverridableStringUnion } from '@mui/types';
import { buildAddressWithZip } from './addressUtils';

export interface NewLocationInputProps {
  value: string;
  onChange: (newValue: string) => void;
  required: boolean;
  size: OverridableStringUnion<'small' | 'medium', AutocompletePropsSizeOverrides>;
  label: string;
}

// Builds a comma-separated address string including zip from Google place address components.
// Format: "street, city, state, zip, country" — compatible with the backend's fallback parser.
export { buildAddressWithZip } from './addressUtils';

const NewLocationInputContent = (props: NewLocationInputProps) => {
  const { value, onChange } = props;
  const [userInput, setUserInput] = useState(value || '');
  const [dirty, setDirty] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(value || '');
  const [geocodedAddresses, setGeocodedAddresses] = useState<string[]>([]);
  // Maps suggestion display text to its placePrediction object for detail fetching on selection
  const predictionMap = useRef<Map<string, unknown>>(new Map());
  // Incremented on every new resolution or user input — stale resolutions are discarded
  const resolutionIdRef = useRef(0);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getConfiguration().REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const handleGeocode = useCallback(
    debounce(async (address: string) => {
      if (!address) {
        setGeocodedAddresses([]);
        predictionMap.current = new Map();
        return;
      }
      const places = await google.maps.importLibrary('places');
      // @ts-expect-error type
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: address
      });
      const newMap = new Map<string, unknown>();
      // @ts-expect-error type
      suggestions.forEach((sugg) => {
        const text: string = sugg.placePrediction.text.toString();
        newMap.set(text, sugg.placePrediction);
      });
      predictionMap.current = newMap;
      setGeocodedAddresses([...newMap.keys()]);
    }, 250),
    []
  );

  useEffect(() => {
    if (isLoaded && process.env.NODE_ENV !== 'test') handleGeocode(userInput);
    return () => {
      handleGeocode.cancel();
    };
  }, [handleGeocode, isLoaded, userInput]);

  const handleChange = (_e: React.SyntheticEvent<Element, Event>, val: string | null) => {
    if (!dirty) setDirty(true);
    if (!val) {
      resolutionIdRef.current++;
      setUserInput('');
      setSelectedAddress('');
      onChange('');
      return;
    }
    const prediction = predictionMap.current.get(val);
    if (prediction) {
      // Selection from suggestions — fetch full place details to include zip in address
      const currentId = ++resolutionIdRef.current;
      setUserInput(val); // show selected text immediately while fetching
      buildAddressWithZip(prediction)
        .then((fullAddress) => {
          if (currentId !== resolutionIdRef.current) return; // user typed again — discard stale result
          setUserInput(fullAddress);
          setSelectedAddress(fullAddress);
          onChange(fullAddress);
        })
        .catch(() => {
          if (currentId !== resolutionIdRef.current) return;
          // Fallback to displayed text if place detail fetch fails
          setSelectedAddress(val);
          onChange(val);
        });
    } else {
      resolutionIdRef.current++;
      setUserInput(val);
      setSelectedAddress(val);
      onChange(val);
    }
  };

  const handleInputChange = (_e: React.SyntheticEvent<Element, Event>, val: string | null) => {
    if (!dirty) setDirty(true);
    // Increment resolution ID to cancel any in-flight place detail fetch
    resolutionIdRef.current++;
    setUserInput(val || '');
    setSelectedAddress(val || '');
    onChange(val || '');
  };

  return (
    <Autocomplete
      value={selectedAddress || userInput}
      inputValue={userInput}
      onInputChange={handleInputChange}
      ListboxProps={{ style: { maxHeight: 150 } }}
      onChange={handleChange}
      onBlur={() => setDirty(true)}
      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      options={geocodedAddresses}
      getOptionLabel={(option: string) => option}
      freeSolo={true}
      sx={{ width: '100%' }}
      size={props.size}
      filterOptions={(options) => options}
      renderInput={(params) => (
        <TextField
          {...params}
          required={props.required}
          InputProps={{
            ...params.InputProps,
            style: { fontSize: '.875rem' }
          }}
          label={props.label}
          className={INPUT_STYLE}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          className="text-sm14 py-1 pl-4 cursor-pointer hover:bg-grey-1">
          {option}
        </li>
      )}
    />
  );
};

export const NewLocationInput = withGoogleMapsApiKey(NewLocationInputContent);

export default NewLocationInput;
